import { useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useDepartments, useSaveDepartment, useDeleteDepartment, errMessage, type Department } from './systemMasters';
import { DepartmentDetailDialog } from './DepartmentDetailDialog';
import { deptTreeOrMatches, toggleIn, treeCell, treeHeader } from './departmentTree';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';

/**
 * The Child Department grid inside the Department detail — mirrors the legacy
 * `Department` self-referencing tree (HCategoryParent-HCategoryChild / Department.Parent).
 * Children = departments whose Parent = this department. New creates a child,
 * Link re-parents an existing department under this one, Unlink clears the parent.
 * On a NEW (unsaved) department Link / Unlink work on a staged set held by the page and committed
 * when the department is saved (legacy lets you link children before the first save); Edit and
 * Delete need the saved parent.
 */
interface Props {
  departmentId?: string;
  /** Children picked for a department that is not saved yet. */
  staged?: Set<string>;
  onStagedChange?: (next: Set<string>) => void;
  /** Edit icon in the LAST column of the Child Department grid (G5 "{name} - Department" popup) instead of the
   *  first. Opt-in, set by DepartmentDetailDialog only — the Department form's own detail keeps its layout. (The
   *  Link picker always has it last, as G5 does everywhere.) */
  editRight?: boolean;
}
export function DepartmentChildPanel({ departmentId, staged, onStagedChange, editRight = false }: Props) {
  const noDept = !departmentId;
  const staging = noDept && !!onStagedChange;
  const stagedIds = staged ?? new Set<string>();
  const notify = useNotify();
  const { confirm } = useConfirm();
  const { data: all = [], isFetching } = useDepartments();
  const save = useSaveDepartment();
  const del = useDeleteDepartment();

  const children = departmentId ? all.filter((d) => d.parentId === departmentId) : staging ? all.filter((d) => stagedIds.has(d.id)) : [];
  // The Link picker lists EVERY department as one tree — the current department, its existing children and its
  // ancestors included — exactly as G5 does (owner report 2026-10-15: leaving the children out cut D2 ▸ Organic
  // out of the tree). What may not be linked is refused on OK, before anything is saved (see doLink).
  const linkable = all;

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [editDept, setEditDept] = useState<Department | null>(null);   // full Department detail dialog for the edited row
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkSel, setLinkSel] = useState<Set<string | number>>(new Set());
  const [linkSearch, setLinkSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [linkCollapsed, setLinkCollapsed] = useState<Set<string>>(new Set());   // picker tree: all expanded to start

  const openEditRow = (r: Department) => { setLinkOpen(false); setEditDept(r); };
  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one department to edit.'); return; }
    const r = children.find((d) => d.id === String([...selectedIds][0])); if (r) openEditRow(r);
  };
  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one department to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'}?` })) return;
    try { for (const id of selectedIds) await del.mutateAsync(String(id)); setSelectedIds(new Set()); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const openLink = () => { setEditDept(null); setLinkSel(new Set()); setLinkSearch(''); setLinkCollapsed(new Set()); setLinkOpen(true); };
  // The picker shows the departments as a tree (G5 parity, owner request 2026-10-15); while searching, the matches flat.
  const linkTree = useMemo(() => deptTreeOrMatches(linkable, linkCollapsed, linkSearch), [linkable, linkCollapsed, linkSearch]);
  const linkRows = linkTree.rows;
  const doLink = async () => {
    if (linkSel.size === 0) { notify.warning('Select at least one department to link.'); return; }
    if (staging) {
      onStagedChange?.(new Set([...stagedIds, ...[...linkSel].map(String)]));
      setLinkOpen(false); setLinkSel(new Set());
      return;
    }
    if ([...linkSel].some((id) => String(id) === departmentId)) { notify.warning('A department cannot be its own child.'); return; }
    // An ancestor (its parent, the parent's parent, …) cannot become its child — that would close a loop. Checked for
    // the WHOLE selection up front, so a refused pick never leaves the others half-linked. Same wording as the API
    // guard (SystemManagementController), which stays the authority.
    const ancestors = new Set<string>();
    for (let p = all.find((d) => d.id === departmentId)?.parentId ?? null; p && !ancestors.has(p); p = all.find((d) => d.id === p)?.parentId ?? null) ancestors.add(p);
    if ([...linkSel].some((id) => ancestors.has(String(id)))) {
      notify.warning('Circular reference detected. To correct this error, set the Parent property to another value.');
      return;
    }
    // A department that is already a child needs no change.
    const toLink = linkable.filter((d) => linkSel.has(d.id) && d.parentId !== departmentId);
    setBusy(true);
    try {
      for (const d of toLink) await save.mutateAsync({ id: d.id, name: d.name ?? '', sort: d.sort, parentId: departmentId! });
      setLinkOpen(false); setLinkSel(new Set());
    } catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one department to unlink.'); return; }
    const ok = await confirm({
      title: 'Unlink', tone: 'danger', confirmLabel: 'Unlink',
      message: `Remove ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'} from this parent? (The department itself is kept.)`,
    });
    if (!ok) return;
    if (staging) {
      onStagedChange?.(new Set([...stagedIds].filter((id) => !selectedIds.has(id))));
      setSelectedIds(new Set());
      return;
    }
    try {
      for (const id of selectedIds) {
        const d = children.find((x) => x.id === String(id));
        if (d) await save.mutateAsync({ id: d.id, name: d.name ?? '', sort: d.sort, parentId: null });
      }
      setSelectedIds(new Set());
    } catch (e) { notify.error(errMessage(e)); }
  };

  const cols: AppColumn<Department>[] = [
    {
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); openEditRow(r); }}><i className="ti ti-edit" /></button>,
    },
    // LIMS Child Department grid: Name + Sort fill the grid roughly equally (Sort right-aligned).
    { field: 'name', header: 'Name', width: '60%', minWidth: 200, text: (r) => r.name ?? '', compare: (a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }) },
    { field: 'sort', header: 'Sort', width: '40%', minWidth: 110, align: 'right', type: 'number', sortable: false, value: (r) => r.sort ?? null, text: (r) => r.sort != null ? String(r.sort) : '' },
  ];
  // LIMS Child Department link popup (G5 parity, owner request 2026-10-15): the departments as a tree — arrow ·
  // tick box · name, children indented under their parent — and the edit icon in the LAST column. The rows come in
  // tree order, so the Department column does not re-sort them.
  const linkCols: AppColumn<Department>[] = [
    { field: 'name', header: treeHeader('Department', (linkSearch.trim() ? linkRows : linkable).map((d) => d.id), linkSel, setLinkSel),
      headerText: 'Department', minWidth: 300, sortable: false, text: (r) => r.name ?? '',
      render: treeCell(linkTree.meta, linkCollapsed, toggleIn(setLinkCollapsed), linkSel, setLinkSel) },
    {
      field: 'edit', header: '', headerText: 'Edit', noExport: true, sortable: false, filterable: false, width: 44, align: 'center', text: () => '',
      render: (r) => <button className="pts-icon-btn edit" title="Edit" tabIndex={-1} onClick={(e) => { e.stopPropagation(); openEditRow(r); }}><i className="ti ti-edit" /></button>,
    },
  ];

  useHotkeys([
    { combo: 'alt+l', label: 'Link', group: 'Child Department', order: 1, disabled: noDept && !staging, run: openLink },
    { combo: 'alt+k', label: 'Unlink', group: 'Child Department', order: 2, disabled: (noDept && !staging) || selectedIds.size === 0, run: () => { void doUnlink(); } },
    { combo: 'alt+e', label: 'Edit', group: 'Child Department', order: 3, disabled: noDept || selectedIds.size !== 1, run: editSelected },
    { combo: 'alt+d', label: 'Delete', group: 'Child Department', order: 4, disabled: noDept || selectedIds.size === 0, run: () => { void removeSelected(); } },
  ]);

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-toolbar">
        <button className="ca-btn" title={`Link (${formatCombo('alt+l')})`} onClick={openLink} disabled={noDept && !staging}><i className="ti ti-link" /> Link</button>
        <button className="ca-btn" title={`Unlink (${formatCombo('alt+k')})`} onClick={() => void doUnlink()} disabled={(noDept && !staging) || selectedIds.size === 0}><i className="ti ti-unlink" /> Unlink</button>
        <button className="ca-btn" title={`Edit (${formatCombo('alt+e')})`} onClick={editSelected} disabled={noDept || selectedIds.size !== 1}><i className="ti ti-edit" /> Edit</button>
        <button className="ca-btn" title={`Delete (${formatCombo('alt+d')})`} onClick={() => void removeSelected()} disabled={noDept || selectedIds.size === 0}><i className="ti ti-trash" /> Delete</button>
        <ExportMenu triggerClassName="ca-btn" filename="ChildDepartment" headers={['Name', 'Sort']} rows={children.map((d) => [d.name ?? '', d.sort != null ? String(d.sort) : ''])} />
      </div>
      {noDept && !staging && <div className="acc-sub-hint">Save the department first, then add or link child departments.</div>}

      <AppDataGrid<Department>
        rows={children} columns={editRight ? [...cols.slice(1), ...cols.slice(0, 1)] : cols} getRowId={(r) => r.id}
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={editDept?.id ?? null}
        initialSort={{ key: 'name', dir: 'asc' }} loading={!noDept && isFetching} emptyText="No data to display"
        defaultPageSize={20}
      />

      {/* Edit pencil → full Department detail form (Department + Parent + Sort + Employees/Child tabs), like LIMS. */}
      {editDept && <DepartmentDetailDialog department={editDept} onClose={() => setEditDept(null)} />}

      {linkOpen && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); setLinkOpen(false); }}>
          <div className="acc-dialog acc-dialog-pick acc-dialog--accent" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>Department</span><i className="ti ti-x acc-dialog-x" onClick={() => setLinkOpen(false)} /></div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search"><div className="acc-search"><i className="ti ti-search" />
                <input placeholder="Search…" autoFocus value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} />
                {linkSearch && <i className="ti ti-x acc-search-x" onClick={() => setLinkSearch('')} />}
              </div></div>
              <AppDataGrid<Department> className="dd-treegrid"
                rows={linkRows} columns={linkCols} getRowId={(r) => r.id}
                selectable selectedIds={linkSel} onSelectionChange={setLinkSel}
                emptyText="No departments available to link."
                defaultPageSize={20}
              />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" disabled={busy || linkSel.size === 0} onClick={() => void doLink()}>OK</button>
              <button className="ca-btn" disabled={busy} onClick={() => setLinkOpen(false)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
